/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import { FileText, X } from "lucide-react";
import { useEffect, useState } from "react";
import { PiMicrosoftExcelLogoLight } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import { formatDate } from "../../../utils/helpers";
import { useAdminAuth } from "../../../context/AdminAuthContext";
const PAYMENT_BADGE_THEME = {
  CASH: "bg-amber-100 text-amber-700 ring-amber-200",
  UPI_QR: "bg-emerald-100 text-emerald-700 ring-emerald-200",
};

const transformInvoiceData = (invoice) => {
  return {
    id: invoice._id,
    invoiceNo: invoice.invoiceNumber,
    date: formatDate(invoice.invoiceDate),
    customer: invoice.customerSnapshot?.name ?? "—",
    branch: invoice.branchSnapshot?.name ?? "—",
    total: invoice.totalAmount,
    paymentMode: invoice.paymentMode,
  };
};

const AdminInvoices = () => {
  const DEFAULT_FILTERS = {
    paymentMode: "",
    fromDate: "",
    toDate: "",
    sortOrder: "desc",
  };
  const LIMIT_PER_PAGE = 20;
  const navigate = useNavigate();
  const { admin, isAdmin, isManager } = useAdminAuth();

  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [selectedBranch, setSelectedBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [branchLoading, setBranchLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: LIMIT_PER_PAGE,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  const fetchInvoices = async () => {
    try {
      const params = {
        page,
        limit: LIMIT_PER_PAGE,
        search: search || undefined,
        paymentMode: appliedFilters.paymentMode || undefined,
        from: appliedFilters.fromDate || undefined,
        to: appliedFilters.toDate || undefined,
        sortOrder: appliedFilters.sortOrder,
      };

      //  Manager = forced branch scope
      if (isManager) {
        params.branchId = admin?.assignedBranch?._id;
      }

      // Admin = optional branch filter
      if (isAdmin && selectedBranch) {
        params.branchId = selectedBranch;
      }

      const res = await axios.get(`${process.env.REACT_APP_API_URL}/invoices`, {
        params,
      });

      setInvoices(res.data.data.map(transformInvoiceData));
      setPagination(res.data.pagination);
    } catch (err) {
      setInvoices([]);
      console.error("Failed to load invoices", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      setBranchLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/branches/get-all`,
      );
      setBranches(res.data.data || []);
    } catch (err) {
      console.error("Failed to load branches", err);
      setBranches([]);
    } finally {
      setBranchLoading(false);
    }
  };

  const activeFilters = Object.entries(appliedFilters)
    .filter(([_, value]) => value && value !== "desc")
    .map(([key, value]) => ({
      key,
      label: key === "sortOrder" ? "Oldest First" : value,
    }));

  useEffect(() => {
    if (isAdmin) {
      fetchBranches();
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchInvoices();
  }, [page, appliedFilters, selectedBranch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchInvoices();
    }, 400); // debounce delay

    return () => clearTimeout(timer);
  }, [search]);

  const handleExportPDF = () => {
    console.log("Export PDF with filters:", appliedFilters);
  };

  const handleExportExcel = () => {
    console.log("Export Excel with filters:", appliedFilters);
  };

  const navigateToDetailsPage = (invoiceId) => {
    if (!invoiceId) return;
    navigate(`/invoices/${invoiceId}`);
  };

  const handleDownloadPDF = (id) => {
    window.open(
      `${process.env.REACT_APP_API_URL}/invoices/pdf/${id}`,
      "_blank",
    );
  };

  return (
    <div className="bg-gradient-to-br from-[#FFF7F3] to-gray-50">
      <div className="px-6 py-2">
        <Breadcrumb title1="Invoices" title2="All amounts are GST inclusive" />
      </div>
      
      {isAdmin && (
        <div className="mx-6 mb-6 bg-white rounded-lg shadow-md border p-2">
          {branchLoading ? (
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : (
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {/* All branches */}
              <button
                onClick={() => {
                  setSelectedBranch("");
                  setPage(1);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  selectedBranch === ""
                    ? "bg-gradient-to-r from-accentTextColor to-accentTextColor/80 text-white shadow"
                    : "bg-gray-100 text-gray-600 hover:bg-brandAccentColor/40"
                }`}
              >
                All
              </button>

              {branches.map((b) => (
                <button
                  key={b._id}
                  onClick={() => {
                    setSelectedBranch(b._id);
                    setPage(1);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    selectedBranch === b._id
                      ? "bg-gradient-to-r from-accentTextColor to-accentTextColor/80 text-white shadow"
                      : "bg-gray-100 text-gray-600 hover:bg-brandAccentColor/40"
                  }`}
                >
                  {b.branchName}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="px-6 space-y-6 ">
        {/* Filters & Sorting */}
        <div className="bg-white border border-[#F7BFAD] rounded-2xl  space-y-3">
          {/* Top Row */}
          <div className="flex flex-wrap gap-4 items-end px-4 py-3">
            {/* Search */}
            <div className="flex flex-col text-sm flex-1 min-w-[260px]">
              <label className="text-xs text-gray-500 mb-2">
                Search (Booking ID / Customer)
              </label>
              <input
                type="text"
                placeholder="Type Booking ID or customer name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-0"
              />
            </div>

            {/* Payment */}
            <div className="flex flex-col text-sm w-40">
              <label className="text-xs text-gray-500 mb-2">Payment</label>
              <select
                value={filters.paymentMode}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    paymentMode: e.target.value,
                  }))
                }
                className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-0"
              >
                <option value="">All</option>
                <option value="CASH">Cash</option>
                <option value="UPI_QR">UPI</option>
              </select>
            </div>

            {/* From */}
            <div className="flex flex-col text-sm w-36">
              <label className="text-xs text-gray-500 mb-2">From</label>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
                }
                className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-0"
              />
            </div>

            {/* To */}
            <div className="flex flex-col text-sm w-36">
              <label className="text-xs text-gray-500 mb-2">To</label>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, toDate: e.target.value }))
                }
                className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-0"
              />
            </div>

            {/* Sort */}
            <div className="flex flex-col text-sm w-40">
              <label className="text-xs text-gray-500 mb-2">Sort</label>
              <select
                value={filters.sortOrder}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, sortOrder: e.target.value }))
                }
                className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-0"
              >
                <option value="desc">Latest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>

            {/* Action */}
            {activeFilters.length > 1 ? (
              <button
                onClick={() => {
                  const reset = {
                    paymentMode: "",
                    fromDate: "",
                    toDate: "",
                    sortOrder: "desc",
                  };

                  setFilters(reset);
                  setAppliedFilters(reset);
                  setSelectedBranch("");
                  setPage(1);
                }}
                className="ml-auto w-36 px-5 py-2 text-sm font-semibold rounded-md border border-red-400 text-red-500 hover:bg-red-50 text-center"
              >
                Clear All
              </button>
            ) : (
              <button
                onClick={() => {
                  setPage(1);
                  setAppliedFilters(filters);
                }}
                className="ml-auto w-36 px-5 py-2 text-sm font-semibold rounded-md bg-[#f0653a] text-white text-center"
              >
                Apply Filters
              </button>
            )}
          </div>

          {/* Second Row: Chips (left) + Export Actions (right) */}
          <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-[#F7BFAD]">
            {/* Chips on the left (only when filters active) */}
            {activeFilters.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {activeFilters.map((f) => (
                  <span
                    key={f.key}
                    className="flex items-center gap-2 px-3 py-1 text-xs rounded-full bg-[#F7BFAD]/40 text-[#f0653a]"
                  >
                    {f.label}
                    <button
                      onClick={() => {
                        const resetValue = f.key === "sortOrder" ? "desc" : "";

                        setFilters((prev) => ({
                          ...prev,
                          [f.key]: resetValue,
                        }));
                        setAppliedFilters((prev) => ({
                          ...prev,
                          [f.key]: resetValue,
                        }));
                        setPage(1);
                      }}
                      className="p-1 hover:bg-[#F7BFAD]/70 rounded-full"
                    >
                      <X className="size-4" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-sm text-gray-500 italic">
                No filters applied — choose at least one filter or select a date
                range to enable export
              </span>
            )}

            {/* Export buttons on the right */}
            <div className="flex gap-3 ml-auto">
              {/* Export PDF */}
              <button
                disabled={activeFilters.length === 0}
                onClick={handleExportPDF}
                className={`flex items-center w-36 gap-2 px-4 py-2 text-sm font-semibold rounded-md border transition
                        ${
                          activeFilters.length === 0
                            ? "text-gray-400 border-gray-300 cursor-not-allowed"
                            : "text-[#f0653a] border-[#f0653a] hover:bg-[#FFF7F3]"
                        }`}
              >
                <FileText className="size-4" />
                <span>Export PDF</span>
              </button>

              {/* Export Excel */}
              <button
                disabled={activeFilters.length === 0}
                onClick={handleExportExcel}
                className={`flex items-center w-36 gap-2 px-4 py-2 text-sm font-semibold rounded-md border transition
                ${
                  activeFilters.length === 0
                    ? "text-gray-400 border-gray-300 cursor-not-allowed"
                    : "text-[#2E7D32] border-[#2E7D32] hover:bg-[#E8F5E9]"
                }`}
              >
                <PiMicrosoftExcelLogoLight className="size-4" />
                <span>Export Excel</span>
              </button>
            </div>
          </div>
        </div>

        {/* Invoice Table */}
        <div className="rounded-2xl shadow-sm border min-h-[20rem] border-[#F7BFAD] bg-white overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-[#F7BFAD]/40 text-[#1f2937]">
              <tr>
                <th className="w-6 text-left px-4 py-3">SL</th>{" "}
                <th className="w-40 text-left px-4 py-3">Invoice No</th>{" "}
                <th className="w-32 text-left px-4 py-3">Date</th>
                <th className="w-56 text-left px-4 py-3">Customer</th>{" "}
                <th className="w-56 text-left px-4 py-3">Branch</th>{" "}
                <th className="w-32 text-left px-4 py-3">Total</th>{" "}
                <th className="w-32 text-center px-4 py-3">Payment</th>{" "}
                <th className="w-36 text-center px-4 py-3">Action</th>{" "}
              </tr>
            </thead>
            {loading ? (
              <InvoiceTableSkeleton rows={10} columns={8} />
            ) : invoices.length === 0 && !loading ? (
              <tbody>
                <tr className="h-[52px]">
                  <td
                    colSpan="8"
                    className="py-8 text-center text-sm text-gray-400"
                  >
                    No invoices found
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {invoices.map((inv, index) => (
                  <tr
                    key={inv.id || index}
                    onClick={() => navigateToDetailsPage(inv.id)}
                    className="h-[52px] border-t border-[#e5e7eb] hover:bg-[#FFF7F3] cursor-pointer"
                  >
                    <td className="px-4 py-3 font-medium ">{index + 1}</td>
                    <td className="px-4 py-3 font-medium text-[#f0653a] truncate">
                      {inv.invoiceNo}
                    </td>
                    <td className="px-4 py-3 text-[#6b7280]">{inv.date}</td>
                    <td
                      className="px-4 py-3 text-[#1f2937] truncate"
                      title={inv.customer}
                    >
                      {inv.customer}
                    </td>
                    <td
                      className="px-4 py-3 text-[#1f2937] truncate"
                      title={inv.branch}
                    >
                      {inv.branch}
                    </td>
                    <td className="px-4 py-3 text-left font-semibold whitespace-nowrap">
                      ₹{inv.total}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={` inline-flex items-center justify-center w-24 h-6 text-xs font-semibold rounded-full ring-1 whitespace-nowrap ${PAYMENT_BADGE_THEME[inv.paymentMode] ?? "bg-gray-100 text-gray-600 ring-gray-200"} `}
                      >
                        {inv.paymentMode === "UPI_QR" ? "UPI" : inv.paymentMode}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadPDF(inv.id);
                        }}
                        title="Download Invoice PDF"
                        className=" inline-flex items-center justify-center h-8 w-8 rounded-md border border-[#f0653a]/40 text-[#f0653a] hover:bg-[#FFF7F3] hover:border-[#f0653a] transition "
                      >
                        <FileText className="h-4 w-4" />
                      </button>
                    </td>{" "}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-end items-center gap-4">
            <button
              disabled={!pagination.hasPrev}
              onClick={() => setPage((p) => p - 1)}
              className="px-4 py-2 text-sm rounded-md border
                 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Prev
            </button>

            <span className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages}
            </span>

            <button
              disabled={!pagination.hasNext}
              onClick={() => setPage((p) => p + 1)}
              className="px-4 py-2 text-sm rounded-md border
                 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}

        {/* Modal placeholder (future detailed API hook) */}
        {selectedInvoice && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl w-full max-w-lg p-6">
              <div className="flex justify-between">
                <h2 className="font-bold">Invoice</h2>
                <button onClick={() => setSelectedInvoice(null)}>Close</button>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                Detailed invoice API will plug in here.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
const InvoiceTableSkeleton = ({ rows = 6, columns = 8 }) => {
  return (
    <tbody>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr
          key={rowIndex}
          className="h-[52px] border-t border-[#e5e7eb] animate-pulse"
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td key={colIndex} className="px-4 py-3">
              <div className="h-4 w-full rounded-md bg-gray-200" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
};

export default AdminInvoices;
